import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { CITIES, CATEGORIES } from "@/lib/constants";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import CategoryBadge from "@/components/CategoryBadge";
import { MapPin, Wallet, Calendar, Search, SlidersHorizontal, X } from "lucide-react";

const STATUSES = ["open", "accepted", "completed"];

export default function MyRequests() {
  const { t, tCat, tCity, tAct } = useLang();
  const [requests, setRequests] = useState([]);
  const [err, setErr] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ q: "", status: "", city: "", category: "", date_from: "", date_to: "" });

  const load = async () => {
    setErr("");
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/requests/mine", { params });
      setRequests(r.data);
    } catch (e) { setErr(errMsg(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.city, filters.category, filters.date_from, filters.date_to]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  const activeCount = useMemo(() =>
    Object.entries(filters).filter(([k, v]) => k !== "q" && v).length
  , [filters]);

  const reset = () => setFilters({ q: "", status: "", city: "", category: "", date_from: "", date_to: "" });

  return (
    <div className="app-shell pb-24" data-testid="my-requests-page">
      <TopBar title={t("myRequests")} back={false} />
      <div className="px-5 pt-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="search-input"
              placeholder={t("searchDescription")}
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="w-full h-11 rounded-full border border-zinc-200 bg-white ps-9 pe-4 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 text-sm"
            />
          </div>
          <button
            data-testid="toggle-filters"
            onClick={() => setShowFilters((s) => !s)}
            className={`relative h-11 px-4 rounded-full inline-flex items-center gap-2 text-sm font-medium transition ${
              showFilters || activeCount > 0 ? "bg-emerald-600 text-white" : "bg-white border border-zinc-200 text-zinc-700"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {activeCount > 0 && <span className="text-xs">{activeCount}</span>}
          </button>
        </div>

        {showFilters && (
          <div className="card-surface p-4 space-y-3" data-testid="filters-panel">
            <div>
              <div className="label-caps mb-2">{t("status")}</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`filter-status-${s}`}
                    onClick={() => setFilters({ ...filters, status: filters.status === s ? "" : s })}
                    className={`pill border transition ${filters.status === s ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-zinc-200 text-zinc-700"}`}
                  >
                    {t(s)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-caps mb-1.5">{t("city")}</div>
                <select
                  data-testid="filter-city"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-2 text-sm"
                >
                  <option value="">{t("all")}</option>
                  {CITIES.map((c) => <option key={c} value={c}>{tCity(c)}</option>)}
                </select>
              </div>
              <div>
                <div className="label-caps mb-1.5">{t("category")}</div>
                <select
                  data-testid="filter-category"
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-2 text-sm"
                >
                  <option value="">{t("all")}</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{tCat(c)}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-caps mb-1.5">{t("dateFrom")}</div>
                <input
                  data-testid="filter-date-from"
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  className="w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-2 text-sm"
                />
              </div>
              <div>
                <div className="label-caps mb-1.5">{t("dateTo")}</div>
                <input
                  data-testid="filter-date-to"
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  className="w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-2 text-sm"
                />
              </div>
            </div>
            {activeCount > 0 && (
              <button data-testid="reset-filters" onClick={reset} className="text-sm text-emerald-700 font-semibold inline-flex items-center gap-1">
                <X className="h-3 w-3" /> {t("resetFilters")}
              </button>
            )}
          </div>
        )}

        {err && <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{err}</div>}
        {requests.length === 0 && !err && <div className="card-surface p-5 text-sm text-zinc-600">{t("noRequests")}</div>}
        <div className="space-y-3">
          {requests.map((r) => (
            <Link key={r.id} to={`/customer/request/${r.id}`} className="card-surface p-4 flex gap-3 items-start" data-testid={`req-card-${r.id}`}>
              <CategoryBadge category={r.category} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold truncate">{tCat(r.category)}{r.activity ? ` · ${tAct(r.activity)}` : ""}</div>
                  <span className={`pill ${r.effective_status === "expired" ? "bg-zinc-200 text-zinc-500" : r.effective_status === "accepted" ? "bg-emerald-600 text-white" : r.effective_status === "offers_received" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}>{t(r.effective_status || r.status)}</span>
                </div>
                <p className="text-sm text-zinc-600 line-clamp-2 mt-0.5">{r.description}</p>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(r.city)}{r.district ? ` — ${r.district}` : ""}</span>
                  <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{r.budget} {t("saudiRiyal")}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{r.deadline}</span>
                  <span className="ms-auto text-emerald-700 font-semibold">{r.offer_count || 0} {t("offers")}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
